import React, { useLayoutEffect, useMemo, useState } from "react";
import { Box, useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { SeatGrid } from "./SeatGrid";
import { SeatStudent } from "./Seat";
import { sortSeatRosterIds, sortSeatRosterStudents } from "./seatLayout";
import { UnsavedArrangementBar } from "./UnsavedArrangementBar";
import { SelectionToolbar } from "./SelectionToolbar";
import { useSaveSeatLayout } from "./useSaveSeatLayout";
import { useStudentBulkActions } from "./useStudentBulkActions";
import { FocusLibraryDrawer } from "@/components/FocusLibraryDrawer";
import SendMessageModal from "@/js/components/SendMessageModal";
import { SendNewTabModal } from "@/js/components/SendNewTabModal";
import { useSeatPlanStore } from "@/stores/v2/seatPlanStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useScreenshareStore } from "@/stores/v2/screenshareStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useStudentClaimsQuery } from "@/query/studentClaimsQuery";
import { useToast } from "@/components/ui/Toast";
import { FocusDTO } from "@/js/api/focus";

interface SeatPlanViewProps {
	students: SeatStudent[];
	cardsByStudent?: Record<string, JSX.Element[]>;
	isClassActive?: boolean;
	onStartScreenshare?: (usernames: string[]) => void;
	onEndScreenshare?: () => void;
	/**
	 * Called after a focus is applied (or updated) from the Focus drawer so the parent can refresh its
	 * applied-focus state immediately, rather than waiting for the next server poll.
	 */
	onFocusApplied?: (focus: FocusDTO) => void;
	/** Whether a focus is currently applied to the class. */
	hasActiveFocus?: boolean;
	/** Stable IDs (stableIdV1) of students currently in the active focus. */
	focusedStudentIds?: string[];
	/** Adds the given students (stableIdV1) to the already-active focus, merging with the existing members. */
	onAddStudentsToActiveFocus?: (stableIds: string[]) => void;
	/** Removes the given students (stableIdV1) from the already-active focus. */
	onRemoveStudentsFromActiveFocus?: (stableIds: string[]) => void;
	/**
	 * Whether students can be selected in the seat plan. When false, the per-seat selection checkboxes
	 * and the floating selection toolbar are suppressed entirely (used by monitor-only classes, where
	 * the seat plan supports arrangement but no student actions).
	 */
	selectionEnabled?: boolean;
	/**
	 * Whether the tiles rendered in the seat plan include the per-student action toolbar row
	 * (chat / open-tab / pause / focus). Regular classes do; monitor-only classes don't, so their
	 * tiles are shorter and empty/loading placeholders must be sized to match (avoids stretching
	 * filled monitor tiles to the taller regular-tile height).
	 */
	tilesHaveActionRow?: boolean;
	/** Releases a student claim for the given qoria user id (regular class only). */
	onReleaseClaim?: (qoriaUserId: string) => void;
	/**
	 * Qoria user ids in the classroom's live activity order (the order it renders its tiles in).
	 * An auto-sorted grid seats students in this order and re-seats them as it changes.
	 */
	activityOrderedIds?: string[];
}

export function SeatPlanView({
	students,
	cardsByStudent,
	isClassActive = false,
	onStartScreenshare,
	onEndScreenshare,
	onFocusApplied,
	hasActiveFocus = false,
	focusedStudentIds,
	onAddStudentsToActiveFocus,
	onRemoveStudentsFromActiveFocus,
	selectionEnabled = true,
	tilesHaveActionRow = true,
	onReleaseClaim,
	activityOrderedIds,
}: SeatPlanViewProps): JSX.Element {
	const { t } = useTranslation();
	const {
		slots,
		columns,
		rows,
		cardWidth,
		hasUnsavedArrangement,
		selectedStudents,
		toggleSelection,
		clearSelection,
		activeId,
		overId,
		pendingMoveIndex,
		beginDrag,
		dragOver,
		endDrag,
		cancelDrag,
		pickUpSeat,
		cancelMove,
		discardArrangement,
		saveArrangement,
		setViewportColumns,
		syncPinnedStudents,
	} = useSeatPlanStore();

	const teacherId = useUserSessionStore().getTeacherId();
	const classroomSourceId = useUserSessionStore().getActiveGroup() as string;
	const classroomStableIdV1 = useSelectedClassroomStore((s) => s.stableIdV1);
	const classroomStudents = useSelectedClassroomStore((s) => s.students);
	const saveSeatLayout = useSaveSeatLayout(teacherId, classroomStableIdV1);
	const { successToast } = useToast();

	// Ordered like the seats below, since the GetClass roster order the strip used to inherit is
	// not guaranteed by the API and can shuffle between refetches.
	const pinnedStudents = useMemo(
		() =>
			sortSeatRosterStudents(
				students.filter((s) => classroomStudents?.find((c) => c.qoriaUserId === s.qoriaUserId)?.pinned),
				activityOrderedIds
			),
		[students, classroomStudents, activityOrderedIds]
	);
	const pinnedIds = useMemo(() => pinnedStudents.map((s) => s.qoriaUserId), [pinnedStudents]);
	// An auto-sorted grid seats students in the classroom's activity order and re-seats them as it
	// changes. A custom layout ignores this and keeps the teacher's arrangement.
	const rosterIds = useMemo(() => sortSeatRosterIds(students, activityOrderedIds), [students, activityOrderedIds]);

	useLayoutEffect(() => {
		syncPinnedStudents(rosterIds, pinnedIds);
	}, [rosterIds, pinnedIds, syncPinnedStudents]);

	const bulk = useStudentBulkActions();
	const screenshareUsernames = useScreenshareStore((s) => s.screenshareStudentUsernames);
	const lockedUsers = useClassroomPolicyStore((s) => s.locked_users);
	const anyScreensharing = screenshareUsernames.size > 0;

	const focusDrawer = useDisclosure();
	const announcementModal = useDisclosure();
	const openTabModal = useDisclosure();
	// Snapshot the targeted usernames when a modal is opened so the recipients stay fixed
	// even if the selection changes (or clears) while the modal is open.
	const [actionRecipients, setActionRecipients] = useState<string[]>([]);

	const claimsQuery = useStudentClaimsQuery({ classroomStableId: classroomStableIdV1 as string });
	const claimedQoriaIds = useMemo(() => {
		const set = new Set<string>();
		(claimsQuery.data ?? []).forEach((c) => {
			if (c.classId !== classroomStableIdV1) set.add(c.studentId);
		});
		return set;
	}, [claimsQuery.data, classroomStableIdV1]);
	const releasableQoriaIds = useMemo(() => {
		const set = new Set<string>();
		(claimsQuery.data ?? []).forEach((c) => {
			if (c.classId === classroomStableIdV1 && c.releasable) set.add(c.studentId);
		});
		return set;
	}, [claimsQuery.data, classroomStableIdV1]);

	const studentsById = useMemo(() => {
		const map: Record<string, SeatStudent> = {};
		students.forEach((s) => (map[s.qoriaUserId] = s));
		return map;
	}, [students]);

	const effectiveSelection = useMemo(
		() => selectedStudents.filter((id) => !!studentsById[id] && !claimedQoriaIds.has(id)),
		[selectedStudents, studentsById, claimedQoriaIds]
	);

	// Counts reflect only the students each action actually affects, e.g. "Add N" excludes
	// students already screensharing, "Pause N" excludes already-paused students, etc.
	const selectedUsernames = bulk.toUsernames(effectiveSelection);
	const pausableCount = selectedUsernames.filter((u) => !lockedUsers.includes(u)).length;
	const resumableCount = selectedUsernames.length - pausableCount;
	const screenshareAddCount = selectedUsernames.filter((u) => !screenshareUsernames.has(u)).length;

	// Focus counts mirror the pause/screenshare counts: only the students each action would actually
	// affect. When a focus is already active, "Add" merges the not-yet-focused students into it (one
	// click, no drawer); "Remove" takes the already-focused selection back out.
	const selectedStableIds = bulk.toStableIds(effectiveSelection);
	const focusedIdSet = new Set(focusedStudentIds ?? []);
	const addableFocusStableIds = selectedStableIds.filter((id) => !focusedIdSet.has(id));
	const removableFocusStableIds = selectedStableIds.filter((id) => focusedIdSet.has(id));
	const focusAddCount = hasActiveFocus ? addableFocusStableIds.length : selectedStableIds.length;
	const focusRemoveCount = removableFocusStableIds.length;

	const handleSave = async () => {
		const layout = saveArrangement();
		try {
			await saveSeatLayout.mutateAsync(layout);
			successToast({ title: t("seatPlan.toastSaved"), isClosable: true });
		} catch (e) {
			console.warn("Failed to persist seat layout", e);
		}
	};

	const handleAnnouncement = () => {
		setActionRecipients(bulk.toUsernames(effectiveSelection));
		announcementModal.onOpen();
	};

	const handleOpenTab = () => {
		setActionRecipients(bulk.toUsernames(effectiveSelection));
		openTabModal.onOpen();
	};

	const recipientUsers = useMemo(() => actionRecipients.map((username) => ({ username })), [actionRecipients]);

	return (
		<Box>
			<SeatGrid
				slots={slots}
				columns={columns}
				rows={rows}
				cardWidth={cardWidth}
				onViewportColumns={setViewportColumns}
				selectionEnabled={selectionEnabled}
				// Bulk actions must not run against a layout the teacher has not committed yet.
				selectionDisabled={hasUnsavedArrangement}
				tilesHaveActionRow={tilesHaveActionRow}
				studentsById={studentsById}
				cardsByStudent={cardsByStudent ?? {}}
				selectedStudents={selectedStudents}
				claimedQoriaIds={claimedQoriaIds}
				releasableQoriaIds={releasableQoriaIds}
				activeId={activeId}
				overId={overId}
				pendingMoveIndex={pendingMoveIndex}
				onToggleSelect={toggleSelection}
				onReleaseClaim={onReleaseClaim}
				onDragStart={beginDrag}
				onDragOver={dragOver}
				onDragEnd={endDrag}
				onDragCancel={cancelDrag}
				onPickUpSeat={pickUpSeat}
				onCancelMove={cancelMove}
				onPlaceSeat={endDrag}
				isClassActive={isClassActive}
				pinnedStudents={pinnedStudents}
			/>

			{hasUnsavedArrangement && <UnsavedArrangementBar onDiscard={discardArrangement} onSave={handleSave} />}

			{selectionEnabled && effectiveSelection.length > 0 && (
				<SelectionToolbar
					count={effectiveSelection.length}
					pausableCount={pausableCount}
					resumableCount={resumableCount}
					screenshareAddCount={screenshareAddCount}
					anyScreensharing={anyScreensharing}
					isClassActive={isClassActive}
					hasActiveFocus={hasActiveFocus}
					focusAddCount={focusAddCount}
					focusRemoveCount={focusRemoveCount}
					onAddFocus={focusDrawer.onOpen}
					onAddToActiveFocus={() => onAddStudentsToActiveFocus?.(addableFocusStableIds)}
					onRemoveFromActiveFocus={() => onRemoveStudentsFromActiveFocus?.(removableFocusStableIds)}
					onPause={() => bulk.pauseStudents(effectiveSelection)}
					onResume={() => bulk.resumeStudents(effectiveSelection)}
					onAnnouncement={handleAnnouncement}
					onOpenTab={handleOpenTab}
					onScreenshare={() => onStartScreenshare?.(Array.from(new Set([...screenshareUsernames, ...selectedUsernames])))}
					onEndScreenshare={() => onEndScreenshare?.()}
					onClear={clearSelection}
				/>
			)}

			<FocusLibraryDrawer
				isOpen={focusDrawer.isOpen}
				closeDrawer={focusDrawer.onClose}
				classIsActive
				preselectedStudentIds={bulk.toStableIds(effectiveSelection)}
				bubbleAppliedOrUpdatedFocus={onFocusApplied}
			/>

			<SendMessageModal
				open={announcementModal.isOpen}
				title={t("seatPlan.announceTitleN", { count: actionRecipients.length })}
				subtitle={t("seatPlan.announceSubtitle")}
				classroomSourceId={classroomSourceId}
				users={recipientUsers}
				maxMessageLength={500}
				onClose={announcementModal.onClose}
			/>

			<SendNewTabModal
				open={openTabModal.isOpen}
				title={t("seatPlan.openTabForNStudents", { count: actionRecipients.length })}
				classroomSourceId={classroomSourceId}
				users={recipientUsers}
				onClose={openTabModal.onClose}
			/>
		</Box>
	);
}
